/**
 * DynSelectedPanel — the Inspector's SELECTED-CARDS pane for a Dynamic section,
 * ported from the monolith's `__dynThumbs` (index.html L10245–10275) plus the
 * header row of `__dynPane` (L12139–12160). 2026-08-20.
 *
 * Three parts, in the monolith's order:
 *   1. a header:  "Homes · 3 selected"        [Choose ›]  [Clear]
 *   2. the picked items as stacked tiles, each with an × to remove
 *   3. "+ Add a home" / "+ Add a floorplan"
 *
 * ONE FLOW, NOT TWO (monolith comment L12211, and its `data-dynopen` handler at
 * L12213: `if(k==="plans"||k==="homes"){window.__dynWiz={sec:…};__dynModal("comms")}`).
 * BOTH the header's "Choose ›" and the "+ Add a …" button open the SAME wizard at
 * the SAME community step. They are not two entry points with two behaviours — the
 * "+ Add" button is a second affordance for the identical flow, which is why this
 * component gives them one `onOpen` and no arguments to tell them apart.
 *
 * COMMUNITY LEVEL IS READ-ONLY (monolith L10250). At `level === "comms"` the "picks"
 * are the featured communities themselves, so the tiles carry no × and the button
 * reads "+ Add / manage communities" — add and remove happen in the community
 * picker, not here. Passing `level="comms"` is what selects that mode.
 *
 * The tile ORDER is the feed's, not the pick order — see `selectedTiles`.
 */
import { selectedTiles, addMoreLabel, paneNoun, type PickKind } from "../services/echDynPick";
import type { Pack, FillLevel } from "../services/dynamicFill";

interface Props {
  /** the section's FEATURE LEVEL — decides which picks the pane is showing */
  level: FillLevel;
  /** loaded content packs (monolith `window.__lvPackCache`) */
  packs: Pack[];
  /** the picks, by kind (monolith `window.__dyn[kind]`) */
  picks: { plans?: string[]; homes?: string[] };
  /** the featured community names, for the read-only comms level (monolith `__lvFeatured()`) */
  featured?: { name: string; code?: string | null }[];
  /** opens the wizard — the SAME call from "Choose ›" and from "+ Add a …" */
  onOpen: () => void;
  /** drop one pick (the tile's ×) */
  onRemove: (key: string) => void;
  /** Clear — back to the template's own content (monolith `data-dynclearsec`, L12158).
   *  Only rendered once something is picked, same as there. OPTIONAL: omitting it
   *  hides the button, which is what a caller with no `__dynOrig` restore path must
   *  do rather than ship a Clear that only pretends to restore (see DynLiveFill). */
  onClear?: () => void;
}

export default function DynSelectedPanel({ level, packs, picks, featured, onOpen, onRemove, onClear }: Props) {
  const kind: PickKind = level === "homes" ? "homes" : level === "plans" ? "plans" : "comms";

  /* ── COMMUNITY LEVEL: read-only preview tiles, managed by the community picker ── */
  if (kind === "comms") {
    const feat = featured || [];
    return (
      <div className="dynp-pane">
        <div className="dynp-ph">
          <span className="dynp-pn">{paneNoun("comms")}</span>
          <span className="dynp-pc">· {feat.length} selected</span>
          <span className="dynp-pspacer" />
          <button type="button" className="dynp-choose" onClick={onOpen}>Choose ›</button>
        </div>
        {feat.map((c) => {
          const p = packs.find((x) => (c.code && x.code === c.code) || String(x.name).toLowerCase() === String(c.name).toLowerCase());
          const url = (p && p.C.community && p.C.community.hero) || "";
          return (
            <div className="dynp-tile" key={c.code || c.name}>
              <span className={"dynp-timg" + (url ? "" : " ph")} style={url ? { backgroundImage: `url('${url}')` } : undefined} />
              <span className="dynp-tn">{c.name}</span>
            </div>
          );
        })}
        <button type="button" className="dynp-add" onClick={onOpen}>+ Add / manage communities</button>
      </div>
    );
  }

  const picked = (kind === "homes" ? picks.homes : picks.plans) || [];
  const tiles = selectedTiles(packs, kind, picked);

  /* the header count is the PICK count, not the tile count. They differ when a pick
     can no longer be resolved — a home that sold, or a community whose pack has not
     loaded yet — and showing the pick count is what the monolith does (L12138:
     `(window.__dyn[kind]||[]).length`). */
  return (
    <div className="dynp-pane">
      <div className="dynp-ph">
        <span className="dynp-pn">{paneNoun(kind)}</span>
        <span className="dynp-pc">· {picked.length} selected</span>
        <span className="dynp-pspacer" />
        <button type="button" className="dynp-choose" onClick={onOpen}>Choose ›</button>
        {!!picked.length && !!onClear && (
          <button type="button" className="dynp-clear" title="Clear selection — back to the template default" onClick={onClear}>Clear</button>
        )}
      </div>

      {tiles.map((t) => (
        <div className="dynp-tile" key={t.key}>
          <span className={"dynp-timg" + (t.img ? "" : " ph")} style={t.img ? { backgroundImage: `url('${t.img}')` } : undefined}>
            <button type="button" className="dynp-tx" title="Remove" onClick={() => onRemove(t.key)}>×</button>
            {!t.img && <span className="dynp-tnoimg">no photo in feed</span>}
          </span>
          <span className="dynp-tn">{t.name}</span>
          <span className="dynp-tm">{t.meta}</span>
        </div>
      ))}

      <button type="button" className="dynp-add" onClick={onOpen}>{addMoreLabel(kind)}</button>
    </div>
  );
}
